<template>
  <q-form ref="formRef" v-bind="$attrs">
    <template v-for="(_, slot) in $slots" v-slot:[slot]="scope">
      <slot :name="slot" v-bind="scope || {}" />
    </template>
  </q-form>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from "vue";
import type { QForm } from "quasar";

interface ValidationComponent {
  hasError: boolean;
  nativeEl?: HTMLElement;
  name?: string;
  validate: () => Promise<boolean>;
}

const formRef = useTemplateRef<QForm>("formRef");

/**
 * Validates only the form components whose `name` attribute matches one of the given names.
 * Returns true if all matched fields pass validation.
 */
const validateByNames = async (
  fieldNames: readonly string[],
): Promise<boolean> => {
  if (formRef.value === null) {
    return false;
  }

  const fieldSet = new Set(fieldNames);

  const components =
    formRef.value.getValidationComponents() as unknown as ValidationComponent[];

  const matching = components.filter(
    (component) => component.name !== undefined && fieldSet.has(component.name),
  );

  await Promise.all(matching.map((component) => component.validate()));

  return matching.every(({ hasError }) => !hasError);
};

defineExpose({
  formRef,
  validateByNames,
});
</script>
